import React from "react";
import { t } from "ttag";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

interface SearchPaginationProps {
    query: string;
    currentPage: number;
    totalPages: number;
}

const pageHref = (query: string, page: number) =>
    `?query=${encodeURIComponent(query)}&page=${page}`;

/**
 * Build the list of page numbers to display, with null representing an
 * ellipsis. Always shows first, last, and a window around the current page.
 */
const buildPageNumbers = (
    currentPage: number,
    totalPages: number,
): (number | null)[] => {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | null)[] = [1];

    if (currentPage > 3) {
        pages.push(null);
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    if (currentPage < totalPages - 2) {
        pages.push(null);
    }

    pages.push(totalPages);
    return pages;
};

export const SearchPagination = ({
    query,
    currentPage,
    totalPages,
}: SearchPaginationProps) => {
    if (totalPages <= 1) return null;

    const pageNumbers = buildPageNumbers(currentPage, totalPages);

    return (
        <nav aria-label={t`Search results pagination`} className={styles.nav}>
            {currentPage > 1 ? (
                <Clickable
                    href={pageHref(query, currentPage - 1)}
                    className={styles.step}
                >
                    <Svg
                        name="caret-right"
                        className={styles.arrowPrev}
                        visuallyHiddenText={t`Previous page`}
                    />
                </Clickable>
            ) : (
                <span className={styles.stepDisabled} aria-hidden="true">
                    <Svg name="caret-right" className={styles.arrowPrev} />
                </span>
            )}

            {pageNumbers.map((page, index) =>
                page === null ? (
                    <span
                        key={`ellipsis-${index}`}
                        className={styles.ellipsis}
                        aria-hidden="true"
                    >
                        &hellip;
                    </span>
                ) : page === currentPage ? (
                    <span
                        key={page}
                        className={styles.currentPage}
                        aria-current="page"
                    >
                        {page}
                    </span>
                ) : (
                    <Clickable
                        key={page}
                        href={pageHref(query, page)}
                        className={styles.pageLink}
                    >
                        {page}
                    </Clickable>
                ),
            )}

            {currentPage < totalPages ? (
                <Clickable
                    href={pageHref(query, currentPage + 1)}
                    className={styles.step}
                >
                    <Svg
                        name="caret-right"
                        className={styles.arrowNext}
                        visuallyHiddenText={t`Next page`}
                    />
                </Clickable>
            ) : (
                <span className={styles.stepDisabled} aria-hidden="true">
                    <Svg name="caret-right" className={styles.arrowNext} />
                </span>
            )}
        </nav>
    );
};
